import React from "react";
import { Box, Container, Typography, useTheme, alpha } from "@mui/material";
import { MotionBox } from "@rusha/shared-theme";
import { JsonLd } from "../common/JsonLd";

interface Metric {
  label: string;
  value: string;
  color: string;
}

const METRICS: Metric[] = [
  { label: "↓ 34% costs", value: "34", color: "#10b981" },
  { label: "↑ 4x deploys", value: "4", color: "#3b82f6" },
  { label: "− 12h/week", value: "12", color: "#7c3aed" },
  { label: "99.99% uptime", value: "99.99", color: "#f59e0b" },
];

const CaseStudy: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      id="case-study"
      aria-labelledby="cs-title"
      sx={{
        py: { xs: 8, md: 10 },
        bgcolor: "background.default",
        position: "relative",
      }}
    >
      <Container maxWidth="md">
        <MotionBox
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{ textAlign: "center" }}
        >
          <Typography
            component="h2"
            id="cs-title"
            sx={{
              fontSize: { xs: "1.5rem", md: "2rem" },
              fontWeight: 700,
              mb: 1,
              color: "text.primary",
              lineHeight: 1.3,
            }}
          >
            Customer story
          </Typography>

          <Box
            component="blockquote"
            sx={{
              m: 0,
              mb: 3,
              px: 0,
              py: 0,
              border: "none",
            }}
          >
            <Typography
              component="p"
              sx={{
                fontSize: { xs: "1.25rem", md: "1.6rem" },
                fontWeight: 700,
                color: "text.primary",
                lineHeight: 1.4,
                mb: 1.5,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              &ldquo;Rusha cut our cloud spend by 34% in 6 weeks&rdquo;
            </Typography>
            <Typography
              component="cite"
              sx={{
                fontSize: "0.95rem",
                color: "text.secondary",
                fontStyle: "normal",
              }}
            >
              — Engineering Lead, Fortune 500 SaaS company
            </Typography>
          </Box>

          {/* Metric chips */}
          <Box
            component="ul"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 2,
              listStyle: "none",
              p: 0,
              m: 0,
            }}
          >
            {METRICS.map((m) => (
              <Box
                key={m.label}
                component="li"
                sx={{
                  listStyle: "none",
                  px: 2.5,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: m.color,
                }}
              >
                <Typography
                  component="data"
                  value={m.value}
                  sx={{
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    color: "inherit",
                  }}
                >
                  {m.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </MotionBox>
      </Container>

      {/* Article + Review + AggregateRating JSON-LD */}
      <JsonLd
        data={{
          "@type": "Article",
          headline: "Rusha cut our cloud spend by 34% in 6 weeks",
          author: { "@type": "Organization", name: "Fortune 500 SaaS company" },
          publisher: { "@type": "Organization", name: "Rusha" },
          datePublished: "2025-01-15",
          url: "https://rusha.io#case-study",
          articleBody:
            "A Fortune 500 SaaS company reduced cloud spend by 34%, increased deployment frequency 4x, saved 12 engineering hours per week, and achieved 99.99% uptime within 6 weeks of adopting Rusha.",
        }}
      />
      <JsonLd
        data={{
          "@type": "Review",
          reviewBody: "Rusha cut our cloud spend by 34% in 6 weeks.",
          author: { "@type": "Person", name: "Engineering Lead" },
          itemReviewed: { "@type": "SoftwareApplication", name: "Rusha" },
          reviewRating: {
            "@type": "Rating",
            ratingValue: "5",
            bestRating: "5",
          },
        }}
      />
      <JsonLd
        data={{
          "@type": "AggregateRating",
          itemReviewed: { "@type": "SoftwareApplication", name: "Rusha" },
          ratingValue: "4.8",
          reviewCount: "27",
          bestRating: "5",
        }}
      />
    </Box>
  );
};

export default CaseStudy;
